import React from 'react';
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  TouchSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';

/**
 * Notion-style sortable list/grid.
 *
 * Props:
 *  - items: array of objects with `id`
 *  - onReorder: (newItems) => void
 *  - renderItem: (item, index, helpers) => ReactNode
 *      helpers = { dragHandleProps, isDragging, DragHandle }
 *      You can either spread {...dragHandleProps} on your own handle element,
 *      or render <DragHandle /> for the default Notion-style icon button.
 *  - layout: 'list' | 'grid' — default 'list'
 *  - className / itemClassName: optional styling
 */
const SortableList = ({
  items,
  onReorder,
  renderItem,
  layout = 'list',
  className = '',
  itemClassName = '',
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex(i => i.id === active.id);
      const newIndex = items.findIndex(i => i.id === over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        onReorder(arrayMove(items, oldIndex, newIndex));
      }
    }
  };

  const strategy = layout === 'grid' ? rectSortingStrategy : verticalListSortingStrategy;

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={items.map(i => i.id)} strategy={strategy}>
        <div className={className}>
          {items.map((item, index) => (
            <SortableRow key={item.id} id={item.id} className={itemClassName}>
              {(helpers) => renderItem(item, index, helpers)}
            </SortableRow>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};

const SortableRow = ({ id, children, className = '' }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : 'auto',
    position: 'relative',
  };

  const dragHandleProps = { ...attributes, ...listeners };

  /* Default Notion-style handle: absolutely positioned on the left, visible on hover.
     Useful for vertical lists. */
  const DragHandle = ({ className: extra = '' }) => (
    <button
      type="button"
      {...dragHandleProps}
      className={`absolute -left-7 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded text-[#9a8a6a] opacity-0 group-hover/sortable:opacity-100 hover:bg-[#f0e8d0]/60 cursor-grab active:cursor-grabbing transition-opacity touch-none ${extra}`}
      aria-label="Arrastrar para reordenar"
      data-testid="sortable-handle"
    >
      <GripVertical className="w-4 h-4" strokeWidth={1.5} />
    </button>
  );

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group/sortable ${className}`}
      data-sortable-id={id}
    >
      {children({ dragHandleProps, isDragging, DragHandle })}
    </div>
  );
};

export default SortableList;
